import { totalLabel } from './scorecardTotals'
import type { CourseHole } from '../../hooks/useCourseBook'

interface CourseScorecardProps {
  /** The course's holes in play order, exactly as the card serves them. */
  holes: CourseHole[]
  /** Rendered as the table caption — "Kissena Park, the front nine". */
  caption: string
}

/**
 * A COURSE's card: par, stroke index and yardage for every hole, with no
 * player rows at all.
 *
 * Eighteen holes are drawn as TWO NINES rather than one eighteen-column table,
 * which is what the paper card does and what fits a phone. Each nine carries
 * its own total column — "Out" then "In" — and the table scrolls inside its own
 * box below that, so the page body never scrolls sideways. A NINE-hole course
 * has neither an out nor an in, so its one column is a "Total" (`totalLabel`,
 * the same rule `RoundScorecard` obeys).
 *
 * `RoundScorecard` is the round page's table — this shape plus a row per
 * player. Both are real and shipped, and they stay UNCOUPLED on purpose. What
 * they genuinely share is one RULE, and it already lives in one place:
 * `totalLabel`, the Out/In-against-Total decision this table got wrong until it
 * was extracted. The rest is two tables that answer different questions — a
 * course's card is a reference a member reads before a round; a round's card is
 * a record of what four people did — and a single table taking optional player
 * rows would have to be designed around both at once. If a THIRD consumer ever
 * appears, that is when unifying earns its keep.
 *
 * A hole with no yardage reads "—". The club measured what it measured.
 */
export function CourseScorecard({ holes, caption }: CourseScorecardProps) {
  const nines = chunkIntoNines(holes)

  return (
    <div className="overflow-x-auto">
      {nines.map((nine, index) => (
        <table
          key={nine[0]?.number ?? index}
          className="w-full border-collapse tabular-nums [&:not(:first-child)]:mt-6"
        >
          <caption className="mb-4 text-left text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep">
            {caption}
            {nines.length > 1 && (index === 0 ? ', the front nine' : ', the back nine')}
          </caption>
          <thead>
            <tr>
              <Head>Hole</Head>
              {nine.map((hole) => (
                <Head key={hole.number}>{hole.number}</Head>
              ))}
              <Head total>{totalLabel(nines.length, index)}</Head>
            </tr>
          </thead>
          <tbody>
            <Row label="Par" nine={nine} value={(hole) => hole.par} total={sumPar(nine)} />
            <Row
              label="SI"
              nine={nine}
              value={(hole) => hole.stroke_index}
              // A stroke index is a rank across the whole course; adding nine
              // of them answers nothing, so the total cell stays empty.
              total={null}
            />
            <Row
              label="Yards"
              nine={nine}
              value={(hole) => hole.yardage ?? '—'}
              total={sumYardage(nine)}
            />
          </tbody>
        </table>
      ))}
    </div>
  )
}

function chunkIntoNines(holes: CourseHole[]): CourseHole[][] {
  const nines: CourseHole[][] = []
  for (let start = 0; start < holes.length; start += 9) {
    nines.push(holes.slice(start, start + 9))
  }
  return nines
}

function sumPar(nine: CourseHole[]): number {
  return nine.reduce((total, hole) => total + hole.par, 0)
}

/** ALL OR NOTHING, like the server's own totals: one unmeasured hole ⇒ no sum. */
function sumYardage(nine: CourseHole[]): string {
  if (nine.some((hole) => hole.yardage === null)) return '—'
  return nine.reduce((total, hole) => total + (hole.yardage ?? 0), 0).toLocaleString('en-US')
}

function Head({ children, total }: { children: React.ReactNode; total?: boolean }) {
  return (
    <th
      scope="col"
      className={`border-b ${total ? 'border-l ' : ''}border-brass px-3.5 pb-2.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep`}
    >
      {children}
    </th>
  )
}

function Row({
  label,
  nine,
  value,
  total,
}: {
  label: string
  nine: CourseHole[]
  value: (hole: CourseHole) => React.ReactNode
  total: React.ReactNode
}) {
  return (
    <tr>
      <th
        scope="row"
        className="border-b border-brass/25 px-3.5 py-3.5 text-left text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep"
      >
        {label}
      </th>
      {nine.map((hole) => (
        <td
          key={hole.number}
          className="border-b border-brass/25 px-3.5 py-3.5 font-display text-sm tabular-nums text-pine"
        >
          {value(hole)}
        </td>
      ))}
      <td className="border-b border-l border-brass/25 px-3.5 py-3.5 font-display text-sm tabular-nums text-pine">
        {total}
      </td>
    </tr>
  )
}
